import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm as useFormHook } from "@/hooks/use-form";
import { FormField } from "@/polymet/components/ui/form-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { DatePicker } from "@/components/ui/date-picker";
import { FileUploader } from "@/polymet/components/file-upload";
import { RFQService, RFQRequest, RFQResponse } from "@/lib/rfq-service";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, InfoIcon } from "lucide-react";

interface RFQResponseFormProps {
  rfq?: RFQRequest;
  rfqId?: string;
  responseId?: string;
  isEditing?: boolean;
  initialResponse?: Partial<RFQResponse>;
  onSuccess?: (response: RFQResponse) => void;
}

export function RFQResponseForm({ rfq, rfqId, responseId, isEditing, initialResponse, onSuccess }: RFQResponseFormProps) {
  const [loadedRfq, setLoadedRfq] = useState<RFQRequest | null>(rfq || null);
  const [attachments, setAttachments] = useState<string[]>(initialResponse?.attachments || []);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!rfq && !!rfqId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [specCompliance, setSpecCompliance] = useState<Record<string, any>>({});
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const rfqService = new RFQService();
  
  // Load RFQ data if not provided directly
  useEffect(() => {
    const loadRfqData = async () => {
      if (!rfqId || loadedRfq) return;
      
      try {
        setLoading(true);
        const rfqData = await rfqService.getRFQById(rfqId);
        if (!rfqData) {
          throw new Error("RFQ not found");
        }
        setLoadedRfq(rfqData);
        
        // If editing an existing response, load it
        if (isEditing && responseId) {
          const response = await rfqService.getRFQResponses(rfqId).then(responses => 
            responses.find(r => r.id === responseId)
          );
          if (response) {
            // Pre-fill the form with existing response data
            // This would be handled by initialResponse in most cases
          }
        }
      } catch (error) {
        console.error("Error loading RFQ data:", error);
        setLoadError("Failed to load RFQ details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadRfqData();
  }, [rfqId, responseId, isEditing, loadedRfq, rfqService]);
  
  // Show loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  
  // Show error message
  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }
  
  // Show message if no RFQ is loaded
  if (!loadedRfq) {
    return (
      <Alert>
        <AlertTitle>No RFQ Selected</AlertTitle>
        <AlertDescription>
          Please select an RFQ to respond to or check the URL parameters.
        </AlertDescription>
      </Alert>
    );
  }
  
  // Initialize spec compliance when RFQ is loaded
  useEffect(() => {
    if (loadedRfq?.specifications) {
      const initialSpecCompliance: Record<string, any> = {};
      Object.keys(loadedRfq.specifications).forEach(key => {
        initialSpecCompliance[key] = {
          complies: true,
          alternativeSpec: ""
        };
      });
      setSpecCompliance(initialSpecCompliance);
    }
  }, [loadedRfq]);
  
  // Initialize spec compliance from rfq specifications
  useEffect(() => {
    if (loadedRfq.specifications) {
      const initialCompliance = { ...loadedRfq.specifications };
      
      // If we have existing compliance data, use that instead
      if (initialResponse?.specifications_compliance) {
        Object.assign(initialCompliance, initialResponse.specifications_compliance);
      }
      
      setSpecCompliance(initialCompliance);
    }
  }, [rfq, initialResponse]);
  
  const validate = (values: Partial<RFQResponse>) => {
    const errors: Record<string, string> = {};
    
    if (!values.quoted_price || values.quoted_price <= 0) {
      errors.quoted_price = "Price must be greater than 0";
    }
    
    if (!values.currency) {
      errors.currency = "Currency is required";
    }
    
    if (!values.delivery_date) {
      errors.delivery_date = "Delivery date is required";
    } else {
      const deliveryDate = new Date(values.delivery_date);
      if (deliveryDate < new Date()) {
        errors.delivery_date = "Delivery date cannot be in the past";
      }
    }
    
    return errors;
  };
  
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue
  } = useForm<RFQResponse>({
    rfq_id: rfq.id || "",
    supplier_id: user?.id || "",
    quoted_price: initialResponse?.quoted_price || 0,
    currency: initialResponse?.currency || "USD",
    delivery_date: initialResponse?.delivery_date || "",
    delivery_fee: initialResponse?.delivery_fee || 0,
    warranty_info: initialResponse?.warranty_info || "",
    payment_terms: initialResponse?.payment_terms || "",
    validity_period: initialResponse?.validity_period || "",
    notes: initialResponse?.notes || "",
    specifications_compliance: initialResponse?.specifications_compliance || {},
    attachments: initialResponse?.attachments || [],
    status: initialResponse?.status || "draft",
  }, validate);
  
  const handleSpecChange = (key: string, value: any) => {
    const updatedCompliance = { ...specCompliance, [key]: value };
    setSpecCompliance(updatedCompliance);
    setFieldValue("specifications_compliance", updatedCompliance);
  };
  
  const handleFileUpload = (uploadedUrls: string[]) => {
    const newAttachments = [...attachments, ...uploadedUrls];
    setAttachments(newAttachments);
    setFieldValue('attachments', newAttachments);
  };
  
  const removeAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
    setFieldValue('attachments', newAttachments);
  };
  
  const handleFormSubmit = async (formValues: RFQResponse) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to submit a quote response",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Ensure supplier_id is set
      formValues.supplier_id = user.id;
      formValues.rfq_id = rfq.id || "";
      
      // Add specifications compliance from state
      formValues.specifications_compliance = specCompliance;
      
      // Add attachments from state
      formValues.attachments = attachments;
      
      // Submit the response
      const response = await rfqService.submitRFQResponse(formValues);
      
      if (response) {
        toast({
          title: "Success",
          description: initialResponse?.id 
            ? "Quote response updated successfully"
            : "Quote response submitted successfully"
        });
        
        if (onSuccess) {
          onSuccess(response);
        } else {
          // Navigate to supplier dashboard
          navigate("/supplier-dashboard/rfqs");
        }
      } else {
        throw new Error("Failed to submit quote response");
      }
    } catch (error) {
      console.error("Error submitting quote response:", error);
      toast({
        title: "Error",
        description: "Failed to submit quote. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={(e) => handleSubmit(e, handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Request Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="font-medium">Title</Label>
                <div>{rfq.title}</div>
              </div>
              
              <div>
                <Label className="font-medium">Description</Label>
                <div className="whitespace-pre-line">{rfq.description}</div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="font-medium">Quantity</Label>
                  <div>{rfq.quantity}</div>
                </div>
                
                {rfq.budget_min && rfq.budget_max && (
                  <div>
                    <Label className="font-medium">Budget Range</Label>
                    <div>${rfq.budget_min} - ${rfq.budget_max}</div>
                  </div>
                )}
                
                {rfq.delivery_date_required && (
                  <div>
                    <Label className="font-medium">Required Delivery Date</Label>
                    <div>{new Date(rfq.delivery_date_required).toLocaleDateString()}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Specifications Required</CardTitle>
            </CardHeader>
            <CardContent>
              {rfq.specifications && Object.keys(rfq.specifications).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(rfq.specifications).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="font-medium">{key}</Label>
                      <div className="flex items-center gap-2">
                        <div>Requested: <span className="font-medium">{value}</span></div>
                        <div className="flex-1">
                          <Input
                            value={specCompliance[key] || ""}
                            onChange={(e) => handleSpecChange(key, e.target.value)}
                            placeholder="Your specification"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground">No specific requirements provided</div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div>
          <FormField
            id="quoted_price"
            label="Quoted Price"
            type="number"
            value={values.quoted_price || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.quoted_price}
            touched={touched.quoted_price}
            required
            min={0}
            step={0.01}
          />
        </div>
        
        <div>
          <Label htmlFor="currency">Currency</Label>
          <select
            id="currency"
            className="w-full rounded-md border border-input bg-background px-3 py-2"
            value={values.currency || "USD"}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="JPY">JPY - Japanese Yen</option>
            <option value="CAD">CAD - Canadian Dollar</option>
            <option value="AUD">AUD - Australian Dollar</option>
            <option value="INR">INR - Indian Rupee</option>
            <option value="CNY">CNY - Chinese Yuan</option>
          </select>
          {errors.currency && touched.currency && (
            <div className="text-red-500 text-sm mt-1">{errors.currency}</div>
          )}
        </div>
        
        <div>
          <FormField
            id="delivery_fee"
            label="Delivery Fee (Optional)"
            type="number"
            value={values.delivery_fee || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.delivery_fee}
            touched={touched.delivery_fee}
            min={0}
            step={0.01}
          />
        </div>
        
        <div>
          <Label htmlFor="delivery_date">Delivery Date</Label>
          <DatePicker
            id="delivery_date"
            value={values.delivery_date ? new Date(values.delivery_date) : undefined}
            onChange={(date) => setFieldValue("delivery_date", date?.toISOString())}
            minDate={new Date()}
          />
          {errors.delivery_date && touched.delivery_date && (
            <div className="text-red-500 text-sm mt-1">{errors.delivery_date}</div>
          )}
        </div>
        
        <div>
          <FormField
            id="warranty_info"
            label="Warranty Information (Optional)"
            value={values.warranty_info || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.warranty_info}
            touched={touched.warranty_info}
            placeholder="e.g., 1 year manufacturer warranty"
          />
        </div>
        
        <div>
          <FormField
            id="payment_terms"
            label="Payment Terms (Optional)"
            value={values.payment_terms || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.payment_terms}
            touched={touched.payment_terms}
            placeholder="e.g., Net 30 days, 50% advance"
          />
        </div>
        
        <div>
          <FormField
            id="validity_period"
            label="Quote Valid Until (Optional)"
            value={values.validity_period || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.validity_period}
            touched={touched.validity_period}
            placeholder="e.g., 30 days from quote date"
          />
        </div>
        
        <div className="md:col-span-2">
          <FormField
            id="notes"
            label="Additional Notes (Optional)"
            value={values.notes || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.notes}
            touched={touched.notes}
            component={Textarea}
            rows={4}
            placeholder="Any additional information about your quote"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="mb-2 block">Attachments (Optional)</Label>
          <FileUploader 
            onUploadComplete={handleFileUpload}
            acceptedFileTypes={[
              'image/jpeg', 
              'image/png', 
              'application/pdf', 
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ]}
            maxFileSizeMB={10}
            maxFiles={5}
          />
          
          {attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <Label>Uploaded Files</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {attachments.map((url, index) => (
                  <div key={index} className="flex items-center justify-between border rounded-md p-2">
                    <div className="truncate max-w-[80%]">
                      {url.split('/').pop()}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="md:col-span-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <InfoIcon className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">
                {values.status === "draft" 
                  ? "Save as draft to continue editing later" 
                  : "Submit your quote to the buyer"}
              </span>
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                type="button"
                variant="secondary"
                disabled={submitting}
                onClick={() => {
                  setFieldValue("status", "draft");
                  handleSubmit(new Event("submit") as any, handleFormSubmit);
                }}
              >
                Save as Draft
              </Button>
              <Button 
                type="button"
                disabled={submitting}
                onClick={() => {
                  setFieldValue("status", "submitted");
                  handleSubmit(new Event("submit") as any, handleFormSubmit);
                }}
              >
                {submitting ? "Submitting..." : "Submit Quote"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
